// src/store/slices/userPanchayathAssignSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";
import { setTenant } from "./tenantSlice";

// helper
const toNumArray = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n > 0);

const hasToken = () => !!localStorage.getItem("token");

// =======================================
// GET tenant tree for Superadmin switcher
// =======================================
export const fetchTenantTreeThunk = createAsyncThunk(
  "userPanAssign/fetchTenantTree",
  async (_, { rejectWithValue, signal, getState }) => {
    try {
      // ✅ guard: never call without token
      if (!hasToken()) {
        return rejectWithValue("NO_TOKEN");
      }

      const role = String(getState()?.auth?.user?.role || "").toLowerCase();
      // If you want only superadmin to call this:
      // if (role !== "superadmin") return rejectWithValue("FORBIDDEN");

      const res = await api.get("/api/superadmin/tenant-tree", { signal });
      const tree = res.data || {};

      const districts = tree.districts || [];
      const taluks = tree.taluks || [];
      const panchayaths = tree.panchayaths || [];
      const linked_pan_ids = toNumArray(tree.linked_pan_ids || []);

      return { districts, taluks, panchayaths, linked_pan_ids };
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return rejectWithValue("REQUEST_CANCELLED");
      }
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to load tenant tree"
      );
    }
  }
);

// ================================
// GET all panchayaths (MASTER)
// ================================
export const fetchAllPanchayathsThunk = createAsyncThunk(
  "userPanAssign/fetchAllPanchayaths",
  async (_, { rejectWithValue, getState, dispatch, signal }) => {
    try {
      // ✅ guard
      if (!hasToken()) return rejectWithValue("NO_TOKEN");

      const res = await api.get("/api/panchayath/panchayaths", { signal });
      const list = res.data?.panchayaths || res.data?.rows || [];

      const role = String(getState()?.auth?.user?.role || "").toLowerCase();
      const isSuperadmin = role === "superadmin";

      // For normal users, if no pan selected yet, auto-select first
      if (!isSuperadmin) {
        const currentPanId = Number(getState()?.tenant?.pan_id || 0);
        if (!currentPanId && list.length > 0) {
          dispatch(
            setTenant({
              pan_id: Number(list[0].pan_id),
              pan_ids: [Number(list[0].pan_id)],
              pan_id_active: Number(list[0].pan_id),
              pan_name: String(list[0].pan_name || ""),
            })
          );
        }
      }

      return list;
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return rejectWithValue("REQUEST_CANCELLED");
      }
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to load panchayaths"
      );
    }
  }
);

// ================================
// GET assigned panchayaths for USER
// ================================
export const fetchUserPanchayathsThunk = createAsyncThunk(
  "userPanAssign/fetchUserPanchayaths",
  async (uid, { rejectWithValue, signal }) => {
    try {
      // ✅ guard
      if (!hasToken()) return rejectWithValue("NO_TOKEN");

      const res = await api.get(`/api/superadmin/users/${uid}/panchayaths`, {
        signal,
      });

      const panIds = toNumArray(res.data?.pan_ids || []);

      const map = {};
      panIds.forEach((id) => {
        map[String(id)] = true;
      });

      return map;
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return rejectWithValue("REQUEST_CANCELLED");
      }
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to load user mapping"
      );
    }
  }
);

// ================================
// SAVE assigned panchayaths
// ================================
export const saveUserPanchayathsThunk = createAsyncThunk(
  "userPanAssign/saveUserPanchayaths",
  async ({ uid, assignedMap }, { rejectWithValue, signal }) => {
    try {
      // ✅ guard
      if (!hasToken()) return rejectWithValue("NO_TOKEN");

      const pan_ids = Object.entries(assignedMap || {})
        .filter(([, v]) => !!v)
        .map(([k]) => Number(k))
        .filter((n) => Number.isFinite(n) && n > 0);

      const res = await api.post(
        `/api/superadmin/users/${uid}/panchayaths`,
        { pan_ids },
        { signal }
      );

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Save failed");
      }

      return true;
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return rejectWithValue("REQUEST_CANCELLED");
      }
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to save mapping"
      );
    }
  }
);

const slice = createSlice({
  name: "userPanAssign",
  initialState: {
    panchayaths: [],
    assignedMap: {},
    loadingPanchayaths: false,
    loadingAssigned: false,
    saving: false,
    error: null,

    // ✅ used by TenantSwitcher (DO NOT clear on assign page unmount)
    tenantTree: {
      districts: [],
      taluks: [],
      panchayaths: [],
      linked_pan_ids: [],
    },
    loadingTenantTree: false,
    currentTenantTreeRequestId: undefined,
  },

  reducers: {
    toggleAssign(state, action) {
      const { pan_id, value } = action.payload;
      state.assignedMap[String(pan_id)] = !!value;
    },

    setAllAssign(state, action) {
      const val = !!action.payload;
      const next = {};
      (state.panchayaths || []).forEach((p) => {
        next[String(p.pan_id)] = val;
      });
      state.assignedMap = next;
    },

    // ✅ only clear Assign page state (NOT tenantTree)
    clearAssignOnly(state) {
      state.panchayaths = [];
      state.assignedMap = {};
      state.loadingPanchayaths = false;
      state.loadingAssigned = false;
      state.saving = false;
      state.error = null;
    },
  },

  extraReducers: (b) => {
    b
      // tenant tree
      .addCase(fetchTenantTreeThunk.pending, (s, a) => {
        s.loadingTenantTree = true;
        s.error = null;
        s.currentTenantTreeRequestId = a.meta.requestId;
      })
      .addCase(fetchTenantTreeThunk.fulfilled, (s, a) => {
        if (s.currentTenantTreeRequestId !== a.meta.requestId) return;
        s.loadingTenantTree = false;
        s.tenantTree =
          a.payload || {
            districts: [],
            taluks: [],
            panchayaths: [],
            linked_pan_ids: [],
          };
        s.currentTenantTreeRequestId = undefined;
      })
      .addCase(fetchTenantTreeThunk.rejected, (s, a) => {
        if (s.currentTenantTreeRequestId !== a.meta.requestId) return;
        s.loadingTenantTree = false;
        s.currentTenantTreeRequestId = undefined;

        // ✅ ignore these
        if (a.payload === "REQUEST_CANCELLED") return;
        if (a.payload === "NO_TOKEN") return;

        s.error = a.payload || "Failed to load tenant tree";
      })

      // all panchayaths
      .addCase(fetchAllPanchayathsThunk.pending, (s) => {
        s.loadingPanchayaths = true;
        s.error = null;
      })
      .addCase(fetchAllPanchayathsThunk.fulfilled, (s, a) => {
        s.loadingPanchayaths = false;
        s.panchayaths = a.payload || [];
      })
      .addCase(fetchAllPanchayathsThunk.rejected, (s, a) => {
        s.loadingPanchayaths = false;
        s.panchayaths = [];

        if (a.payload === "REQUEST_CANCELLED") return;
        if (a.payload === "NO_TOKEN") return;

        s.error = a.payload || "Failed to load panchayaths";
      })

      // assigned
      .addCase(fetchUserPanchayathsThunk.pending, (s) => {
        s.loadingAssigned = true;
        s.error = null;
        s.assignedMap = {};
      })
      .addCase(fetchUserPanchayathsThunk.fulfilled, (s, a) => {
        s.loadingAssigned = false;
        s.assignedMap = a.payload || {};
      })
      .addCase(fetchUserPanchayathsThunk.rejected, (s, a) => {
        s.loadingAssigned = false;
        s.assignedMap = [];

        if (a.payload === "REQUEST_CANCELLED") return;
        if (a.payload === "NO_TOKEN") return;

        s.error = a.payload || "Failed to load mapping";
      })

      // save
      .addCase(saveUserPanchayathsThunk.pending, (s) => {
        s.saving = true;
        s.error = null;
      })
      .addCase(saveUserPanchayathsThunk.fulfilled, (s) => {
        s.saving = false;
      })
      .addCase(saveUserPanchayathsThunk.rejected, (s, a) => {
        s.saving = false;

        if (a.payload === "REQUEST_CANCELLED") return;
        if (a.payload === "NO_TOKEN") return;

        s.error = a.payload || "Failed to save mapping";
      });
  },
});

export const { toggleAssign, setAllAssign, clearAssignOnly } = slice.actions;
export default slice.reducer;
